/**
 * Hook para buscar negócios de um cliente específico via RPC
 * Usado na aba Negócios do modal premium de cliente
 * 
 * @version 2.0.0
 * RELEASE: 2026-01-12 - Migrado para RPC get_customer_deals com fallback de avatar
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('CustomerDeals');

/**
 * Interface do retorno da RPC get_customer_deals
 * Estrutura flat retornada pelo banco
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-12
 */
interface CustomerDealRpcResult {
  id: string;
  title: string;
  value: number;
  currency: string;
  status: string;
  priority: string;
  probability: number | null;
  expected_close_date: string | null;
  actual_close_date: string | null;
  created_at: string;
  // Pipeline
  pipeline_id: string | null;
  pipeline_name: string | null;
  // Workspace
  workspace_id: string | null;
  workspace_name: string | null;
  workspace_color: string | null;
  // Fase
  phase_id: string | null;
  phase_name: string | null;
  phase_color: string | null;
  phase_sort_order: number | null;
  // Vendedor (avatar já com fallback aplicado na RPC)
  seller_id: string | null;
  seller_name: string | null;
  seller_avatar: string | null;
}

/**
 * Interface do deal retornado (estrutura aninhada para compatibilidade)
 * Inclui workspace via pipeline e data de fechamento real
 * 
 * @version 2.0.0
 * RELEASE: 2026-01-12 - Reestruturado para usar RPC
 */
export interface CustomerDeal {
  id: string;
  title: string;
  value: number;
  currency: string;
  status: 'open' | 'won' | 'lost';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  expected_close_date: string | null;
  actual_close_date: string | null;
  probability: number | null;
  created_at: string;
  pipeline: {
    id: string;
    name: string;
    workspace: {
      id: string;
      name: string;
      color: string;
    } | null;
  } | null;
  phase: {
    id: string;
    name: string;
    color: string;
    /** Posição da fase no pipeline para calcular progresso */
    sort_order: number;
  } | null;
  /** Vendedor responsável pelo negócio (avatar já com fallback) */
  seller: {
    id: string;
    name: string;
    avatar_url: string | null;
  } | null;
}

/**
 * Opções do hook para lazy loading
 */
interface CustomerDealsOptions {
  enabled?: boolean;
  /** v2.1.0: filtra negócios vinculados a um veículo (crm_deal_vehicles) */
  vehicleId?: string | null;
}

/**
 * Transforma resultado flat da RPC em estrutura aninhada CustomerDeal
 * Mantém compatibilidade com componentes existentes
 * 
 * @param row - Resultado da RPC get_customer_deals
 * @returns CustomerDeal com estrutura aninhada
 */
function transformCustomerDealRpcResult(row: CustomerDealRpcResult): CustomerDeal {
  return {
    id: row.id,
    title: row.title,
    value: row.value,
    currency: row.currency,
    status: row.status as 'open' | 'won' | 'lost',
    priority: row.priority as 'low' | 'medium' | 'high' | 'urgent',
    expected_close_date: row.expected_close_date,
    actual_close_date: row.actual_close_date,
    probability: row.probability,
    created_at: row.created_at,
    // Pipeline com workspace aninhado
    pipeline: row.pipeline_id ? {
      id: row.pipeline_id,
      name: row.pipeline_name || '',
      workspace: row.workspace_id ? {
        id: row.workspace_id,
        name: row.workspace_name || '',
        color: row.workspace_color || '#6B7280',
      } : null,
    } : null,
    // Fase
    phase: row.phase_id ? {
      id: row.phase_id,
      name: row.phase_name || '',
      color: row.phase_color || '#6B7280',
      sort_order: row.phase_sort_order || 0,
    } : null,
    // Vendedor com avatar já com fallback (vem da RPC)
    seller: row.seller_id ? {
      id: row.seller_id,
      name: row.seller_name || '',
      avatar_url: row.seller_avatar, // Já com COALESCE aplicado na RPC
    } : null,
  };
}

/**
 * Hook para buscar negócios de um cliente via RPC
 * Implementa lógica de fallback: customer_id → contact_id (na RPC)
 * Suporta lazy loading via options.enabled
 * 
 * @version 2.0.0
 * RELEASE: 2026-01-12 - Migrado para RPC com fallback de avatar
 * 
 * @param customerId - ID do cliente (crm_customers.id)
 * @param contactId - ID do contato para fallback (crm_customers.main_contact_id)
 * @param options - Opções de configuração
 */
export function useCustomerDeals(
  customerId: string | null,
  contactId?: string | null,
  options?: CustomerDealsOptions
) {
  const { currentTenant } = useAuth();
  
  // Determina se query deve executar (lazy loading)
  // Precisa de pelo menos customerId OU contactId
  const vehicleId = options?.vehicleId ?? null;
  const hasValidId = !!customerId || !!contactId || !!vehicleId;
  const isEnabled = (options?.enabled ?? true) && hasValidId && !!currentTenant?.id;

  return useQuery({
    queryKey: ['customer-deals', customerId, contactId, vehicleId],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      if (!customerId && !contactId && !vehicleId) return [];

      logger.debug('Buscando negócios via RPC:', { customerId, contactId, vehicleId });

      // Chamada RPC com validação de tenant e fallback customer_id → contact_id
      const { data, error } = await (supabase.rpc as any)('get_customer_deals', {
        p_customer_id: customerId,
        p_contact_id: contactId,
        p_vehicle_id: vehicleId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar negócios via RPC:', error.message);
        throw error;
      }

      // Transformar resultados flat em estrutura aninhada
      const deals = (data || []).map((row: CustomerDealRpcResult) => 
        transformCustomerDealRpcResult(row)
      );

      logger.debug(`Encontrados ${deals.length} negócios para o cliente`);
      return deals as CustomerDeal[];
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // Cache de 30 segundos
  });
}
